import {
  useAsync,
  useDeepCompareEffect,
  useDeepCompareMemo,
  useIsomorphicLayoutEffect,
  useMountEffect,
  usePrevious,
  useSyncedRef,
} from "@react-hookz/web";
import { useSetAtom } from "jotai";
import first from "lodash/first";
import { useRouter } from "next/router";
import qs from "qs";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Disposable } from "react-relay";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";

import { tracking } from "shared/data";
import { article as articleData } from "shared/data/article";
import { notEmpty } from "shared/lib/utils";

import { isInfiniteScrollContentAtom } from "scmp-app/components/article/article-list/atoms";
import { DefaultCustomContents } from "scmp-app/components/article/article-render/consts";
import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { StyleCustomContents } from "scmp-app/components/article/article-render/style-article/consts";
import { checkIsStyleArticle } from "scmp-app/components/article/article-render/style-article/helper";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { useTrackCurrentItem } from "scmp-app/lib/current-item";
import type { articlePaginationCheckerContent$key } from "scmp-app/queries/__generated__/articlePaginationCheckerContent.graphql";
import type {
  articlePaginationNavigationHistoryContent$data,
  articlePaginationNavigationHistoryContent$key,
} from "scmp-app/queries/__generated__/articlePaginationNavigationHistoryContent.graphql";
import type {
  articlePaginationQuery,
  articlePaginationQuery$data,
  BodyFormatTypeEnum,
} from "scmp-app/queries/__generated__/articlePaginationQuery.graphql";

import type { ArticleRecommendation } from "./types";

export const useArticlePagination = (
  entityIds: string[],
  articleMap: ArticleRecommendation["results"]["infiniteScrollArticleMap"],
) => {
  const environment = useRelayEnvironment();

  const [results, setResults] = useState<
    { data: articlePaginationQuery$data; disposable: Disposable }[]
  >([]);
  const data = useMemo(
    () => results.map(result => first(result.data.contents.edges)?.node),
    [results],
  );

  const latestEntityIds = useSyncedRef(entityIds);
  const [currentIndex, setCurrentIndex] = useState(0);
  const latestCurrentIndex = useSyncedRef(currentIndex);

  const [isLoading, setIsLoading] = useState(false);
  const latestIsLoading = useSyncedRef(isLoading);

  const hasNext = useMemo(
    () => entityIds.length > 0 && entityIds.length !== currentIndex,
    [currentIndex, entityIds.length],
  );
  const latestHasNext = useSyncedRef(hasNext);

  const loadNext = useDeepCompareMemo(
    () => async () => {
      if (latestIsLoading.current || !latestHasNext.current) return;

      try {
        const entityId = entityIds[latestCurrentIndex.current];
        const query = graphql`
          query articlePaginationQuery(
            $entityId: String!
            $customContents: [CustomContentInput]
            $bodyType: BodyFormatTypeEnum
          ) {
            contents(filter: { entityIds: [$entityId] }, contentTypes: [ARTICLE, VIDEO], first: 1) {
              edges {
                node {
                  entityId
                  ...articleDividerArticle
                  ...articlePaginationCheckerContent
                  ...articlePaginationNavigationHistoryContent
                    @arguments(customContents: $customContents, bodyType: $bodyType)
                  ...articlePianoPaywallContent
                  ...articleRenderArticle
                    @arguments(customContents: $customContents, bodyType: $bodyType)
                  ...contextsContentReadMoreContextProviderContent
                  ...helpersCheckIsStyleArticle
                  ...helpersRosettaArticleContextArticle
                }
              }
            }
          }
        `;

        const article = articleMap?.[entityId];

        const variables = {
          bodyType: (article && checkIsStyleArticle(article)
            ? "PARAGRAPHANDMEDIA"
            : "PARAGRAPH") as BodyFormatTypeEnum,
          customContents:
            article && checkIsStyleArticle(article) ? StyleCustomContents : DefaultCustomContents,
          entityId,
        };
        const queryRequest = getRequest(query);
        const queryDescriptor = createOperationDescriptor(queryRequest, variables);
        setIsLoading(true);
        const fetched = await fetchQuery<articlePaginationQuery>(
          environment,
          query,
          variables,
        ).toPromise();

        if (entityIds !== latestEntityIds.current) return;

        if (fetched) {
          const disposable = environment.retain(queryDescriptor);
          setResults(results => [...results, { data: fetched, disposable }]);
        }
      } finally {
        setCurrentIndex(index => index + 1);
        setIsLoading(false);
      }
    },
    [entityIds],
  );

  useDeepCompareEffect(() => {
    setIsLoading(false);
    setResults(results => {
      results.forEach(result => result.disposable.dispose());
      return [];
    });
    setCurrentIndex(0);
  }, [entityIds]);

  return { data, hasNext, isLoading, loadNext };
};

export const useTrackPaginationCurrentContent = <T>(items: NonEmptyArray<T>) => {
  const itemElementReferences = useRef<(Element | null)[]>([]);
  const firstItem = items[0];
  const [currentContent, setCurrentContent] = useState<T>(firstItem);

  useIsomorphicLayoutEffect(() => {
    const intersectedIndexes = new Set<number>();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const entryIndex = itemElementReferences.current.indexOf(entry.target);
          if (entry.isIntersecting) {
            intersectedIndexes.add(entryIndex);
          } else {
            intersectedIndexes.delete(entryIndex);
          }
          const intersectedItem = items.at(Math.max(...intersectedIndexes.values()));
          if (intersectedItem) {
            setCurrentContent(intersectedItem);
          }
        });
      },
      { rootMargin: "0px 0px -50% 0px" },
    );

    itemElementReferences.current.forEach(reference => {
      if (!reference) return;
      observer.observe(reference);
    });

    return () => {
      itemElementReferences.current = [];
      observer.disconnect();
    };
  }, [firstItem, items]);

  const bindItemReference = (index: number) => (element: Element | null) => {
    itemElementReferences.current[index] = element;
  };

  return {
    bindItemReference,
    currentContent,
    itemElementReferences,
  };
};

export const useHandlePaginationNavigationHistory = (
  currentContent_: articlePaginationNavigationHistoryContent$key,
  articleRecommendation: ArticleRecommendation,
) => {
  const currentContent = useFragment(
    graphql`
      fragment articlePaginationNavigationHistoryContent on Content
      @argumentDefinitions(
        customContents: { type: "[CustomContentInput]", defaultValue: [] }
        bodyType: { type: "BodyFormatTypeEnum", defaultValue: PARAGRAPH }
      ) {
        entityId
        urlAlias
        ...hooksTrackCurrentItemBase
          @arguments(customContents: $customContents, bodyType: $bodyType)
      }
    `,
    currentContent_,
  );
  const currentContentUrlAlias = currentContent.urlAlias;
  const previousContentUrlAlias = usePrevious(currentContent.urlAlias);

  const [hasPushedHistory, setHasPushedHistory] = useState(false);
  const latestHasPushedHistory = useSyncedRef(hasPushedHistory);
  const router = useRouter();
  const { trackCurrentItem } = useTrackCurrentItem();

  const setIsInfiniteScrollContent = useSetAtom(isInfiniteScrollContentAtom);

  const [_, { execute: pushHistory }] = useAsync(
    async (
      currentContentUrlAlias: string,
      currentItem: articlePaginationNavigationHistoryContent$data,
      articleRecommendation: ArticleRecommendation,
    ) => {
      const { entityId } = currentItem;
      const position = articleRecommendation.results.infiniteScrollArticleIds.indexOf(entityId);
      const contentUrl = new URL(currentContentUrlAlias, location.href);

      const updatedQueryParameters = {
        module:
          position === -1
            ? "perpetual_scroll_0"
            : `perpetual_scroll_${position + 1}_${
                articleRecommendation.firstFilteredMoreOnThisArticleEntityId === entityId
                  ? "RM"
                  : "AI"
              }`,
        pgtype: tracking.pageType.Article,
      };

      contentUrl.search = qs.stringify(updatedQueryParameters);

      const isInfiniteScroll = position >= 0;
      setIsInfiniteScrollContent(isInfiniteScroll);

      const { pathname, push, query } = router;
      await push({ pathname, query }, `${contentUrl.pathname}${contentUrl.search}`, {
        scroll: false,
        shallow: true,
      });
      trackCurrentItem(currentItem);
      setHasPushedHistory(true);
    },
  );

  useMountEffect(() => {
    trackCurrentItem(currentContent);
    return () => trackCurrentItem();
  });

  useEffect(() => {
    if (!previousContentUrlAlias || currentContentUrlAlias === previousContentUrlAlias) {
      return;
    }
    void pushHistory(currentContentUrlAlias, currentContent, articleRecommendation);
  }, [
    currentContentUrlAlias,
    previousContentUrlAlias,
    currentContent,
    pushHistory,
    trackCurrentItem,
    articleRecommendation,
  ]);

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (latestHasPushedHistory.current) {
        window.location.href = as;
        return false;
      }
      return true;
    });
  }, [latestHasPushedHistory, router]);
};

export const useContentPaginationChecker = (
  items: NonEmptyArray<articlePaginationCheckerContent$key>,
) => {
  const lastItem = useFragment<articlePaginationCheckerContent$key>(
    graphql`
      fragment articlePaginationCheckerContent on Content {
        ...helpersGetArticleTypeArticle
        ...helpersUseContentSponsorContent
      }
    `,
    items.at(-1) ?? null,
  );

  const mainArticleType = notEmpty(lastItem) && getArticleType(lastItem);
  const disabledArticleTypes = new Set<string>([
    articleData.types.live.entityId,
    articleData.types.studio.entityId,
  ]);

  const isSponsorArticle = notEmpty(lastItem) && notEmpty(getContentSponsor(lastItem)?.name);

  const shouldStopPagination =
    (mainArticleType && disabledArticleTypes.has(mainArticleType)) || isSponsorArticle;

  return { shouldStopPagination };
};

type NonEmptyArray<T> = [T, ...T[]];
